// Timezone-safe date helpers.
//
// `new Date("2026-05-04")` parses as UTC midnight, so when the user is east
// or west of UTC, `.getDay()` / `.toLocaleDateString()` can land on the
// wrong calendar day. These helpers anchor the parse at LOCAL noon — far
// enough from midnight that no timezone offset can shift the date.
//
// Phase 44d — `todayInOrgIso(tz)` returns "today" in the organisation's
// IANA timezone, regardless of where the user's phone or laptop is. This
// is what every default-date initialiser should call so a coordinator
// travelling abroad still sees the Kenyan calendar day as "today".

/** Parse a "YYYY-MM-DD" string into a Date pinned at local noon. */
export const parseLocalDate = (iso) => {
  if (!iso) return new Date();
  // Already includes a time component → trust it as-is.
  if (typeof iso === "string" && iso.length > 10) return new Date(iso);
  return new Date(`${iso}T12:00:00`);
};

/** Today's date as YYYY-MM-DD in the user's local timezone. */
export const todayLocalIso = () => {
  const d = new Date();
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Today's date as YYYY-MM-DD in the *organisation's* IANA timezone.
 * Falls back to the user's local TZ if the org tz is missing or invalid. */
export const todayInOrgIso = (tz) => {
  if (!tz) return todayLocalIso();
  try {
    // en-CA produces YYYY-MM-DD which is exactly the shape we want.
    return new Date().toLocaleDateString("en-CA", { timeZone: tz });
  } catch {
    return todayLocalIso();
  }
};

/** Convert a Date object to YYYY-MM-DD using the user's LOCAL date parts.
 * Use this instead of `.toISOString().slice(0, 10)` when serialising a
 * picked calendar day — `.toISOString()` returns the UTC date which can
 * be off-by-one for users not on UTC. */
export const toLocalIso = (d) => {
  if (!d) return "";
  if (typeof d === "string") return d.length > 10 ? d.slice(0, 10) : d;
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
};

/** Add `n` days to a YYYY-MM-DD string and return YYYY-MM-DD. Timezone-safe. */
export const addDaysIso = (iso, n) => {
  const d = parseLocalDate(iso);
  d.setDate(d.getDate() + n);
  return toLocalIso(d);
};

/** Format a YYYY-MM-DD for display. Defaults to "Mon, May 4, 2026". */
export const formatLocalDate = (iso, opts = { weekday: "short", year: "numeric", month: "short", day: "numeric" }) => {
  if (!iso) return "";
  return parseLocalDate(iso).toLocaleDateString(undefined, opts);
};
